// src/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import oktaAuth from './oktaService';

interface AuthContextType {
  authState: any;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    const checkAuthentication = async () => {
      const authenticated = await oktaAuth.isAuthenticated();
      setIsAuthenticated(authenticated);
      if (authenticated) {
        const state = await oktaAuth.authStateManager.getAuthState();
        setAuthState(state);
      }
    };

    checkAuthentication();
  }, []);

  const login = async () => {
    await oktaAuth.signInWithRedirect();
  };

  const logout = async () => {
    await oktaAuth.signOut();
  };

  return (
    <AuthContext.Provider value={{ authState, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface PrivateRouteProps {
  component: React.ComponentType;
  [key: string]: any; // To allow any additional props, such as path, etc.
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ component: Component, ...rest }) => {
  const accessToken = sessionStorage.getItem('accessToken');

  if (!accessToken) {
    return <Navigate to="/" />;
  }

  return <Component {...rest} />;
};
